import React, { useEffect, useState, useRef } from "react";
import "./OnlineSubmission.css";
import { generateCaptchaCode, validateCaptcha } from "../../../utiles/generateCaptcha";
import { FaSync } from "react-icons/fa";
import { getNames } from "country-list";
import { ApiUrl } from "../../../Admin/API/ApiUrl";
import Swal from "sweetalert2";

export default function OnlineSubmission() {
  const [captcha, setCaptcha] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [formData, setFormData] = useState({
    paperTitle: "",
    authorName: "",
    authorEmail: "",
    authorInfo: "",
    address: "",
    country: "",
    paperType: "",
    reviewer: "",
    copyrightFile: null,
    paperFile: null,
    abstract: "",
    keywords: "",
    captcha: "",
  });

  //  Initially no authors (empty array)
  const [authors, setAuthors] = useState([]);

  const [errors, setErrors] = useState({});
  const countries = getNames();
  const paperRef = useRef(null);
  const copyrightRef = useRef(null);

  useEffect(() => {
    setCaptcha(generateCaptchaCode());
  }, []);

  const refreshCaptcha = () => {
    setLoading(true);

    // simulate loading delay
    setTimeout(() => {
      setCaptcha(generateCaptchaCode());
      setLoading(false);
    }, 500);
  };


  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  // Handle author input change
  const handleAuthorChange = (index, field, value) => {
    let cleanedValue = value;

    if (field === "name") {
      cleanedValue = value.replace(/[^A-Za-z\s.]/g, "");
    }

    if (field === "email") {
      cleanedValue = value; // don't restrict typing email too much
    }

    const updatedAuthors = [...authors];
    updatedAuthors[index][field] = cleanedValue;

    setAuthors(updatedAuthors);

    let newErrors = { ...errors };

    if (field === "name") {
      if (!cleanedValue.trim()) {
        newErrors[`authorName${index}`] = "Name required";
      } else if (!nameRegex.test(cleanedValue)) {
        newErrors[`authorName${index}`] = "Only letters allowed";
      } else {
        delete newErrors[`authorName${index}`];
      }
    }

    if (field === "email") {
      if (!cleanedValue.trim()) {
        newErrors[`authorEmail${index}`] = "Email required";
      } else if (!emailRegex.test(cleanedValue)) {
        newErrors[`authorEmail${index}`] = "Invalid email";
      } else {
        delete newErrors[`authorEmail${index}`];
      }
    }

    setErrors(newErrors);
  };

  // Add new author
  const addAuthor = () => {
    setAuthors([...authors, { name: "", email: "" }]);
  };

  // Remove author
  const removeAuthor = (index) => {
    const updatedAuthors = authors.filter((_, i) => i !== index);
    setAuthors(updatedAuthors);
  };


  const nameRegex = /^[A-Za-z\s.]+$/; // Only letters
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|in)$/;
  const titleRegex = /^[A-Za-z0-9\s.,-]+$/;
  const reviewerRegex = /^[A-Za-z\s.]+$/;
  const addressRegex = /^[A-Za-z0-9\s,.-]+$/;
  const keywordsRegex = /^[A-Za-z0-9,\s]+$/;


  const validate = () => {
    let newErrors = {};

    // Paper Title
    if (!formData.paperTitle.trim()) {
      newErrors.paperTitle = "Paper title is required";
    } else if (!titleRegex.test(formData.paperTitle)) {
      newErrors.paperTitle = "No special characters allowed in title";
    }

    // Author Name
    if (!formData.authorName.trim()) {
      newErrors.authorName = "Author name is required";
    } else if (!nameRegex.test(formData.authorName)) {
      newErrors.authorName = "Only letters allowed in name";
    }

    // Email
    if (!formData.authorEmail.trim()) {
      newErrors.authorEmail = "Email is required";
    } else if (!emailRegex.test(formData.authorEmail)) {
      newErrors.authorEmail = "Invalid email format";
    }

    // Reviewer
    if (!formData.reviewer.trim()) {
      newErrors.reviewer = "Reviewer info required";
    } else if (!reviewerRegex.test(formData.reviewer)) {
      newErrors.reviewer = "Only letters allowed in reviewer name";
    }

    // Address
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    } else if (!addressRegex.test(formData.address)) {
      newErrors.address = "Invalid characters in address";
    }

    // Paper Type (NO NUMBERS)
    if (!formData.paperType) {
      newErrors.paperType = "Paper type is required";
    } else if (!/^[A-Za-z\s]+$/.test(formData.paperType)) {
      newErrors.paperType = "Invalid paper type";
    }

    // // Keywords
    // if (formData.keywords && !keywordsRegex.test(formData.keywords)) {
    //   newErrors.keywords = "Keywords should not contain special characters";
    // }
    if (!formData.copyrightFile)
      newErrors.copyrightFile = "Copyright file required";
    if (!formData.paperFile) newErrors.paperFile = "Paper file required";

    const errorMsg = validateCaptcha(formData.captcha, captcha);
    if (errorMsg) {
      newErrors.captcha = errorMsg;
    }

    // Authors
    authors.forEach((author, i) => {
      if (!author.name.trim()) {
        newErrors[`authorName${i}`] = `Author ${i + 1} name required`;
      } else if (!nameRegex.test(author.name)) {
        newErrors[`authorName${i}`] = `Only letters allowed (Author ${i + 1})`;
      }

      if (!author.email.trim()) {
        newErrors[`authorEmail${i}`] = `Author ${i + 1} email required`;
      } else if (!emailRegex.test(author.email)) {
        newErrors[`authorEmail${i}`] = `Invalid email (Author ${i + 1})`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      // Swal.fire({
      //   icon: "error",
      //   title: "Validation Error",
      //   text: "Please fill all required fields correctly.",
      // });
      return;
    }

    try {
      const submissionData = new FormData();

      // Main fields
      submissionData.append("paper_title", formData.paperTitle);
      submissionData.append("corresponding_author_name", formData.authorName);
      submissionData.append("corresponding_author_email", formData.authorEmail);
      submissionData.append("secondary_info", formData.authorInfo);
      submissionData.append("address", formData.address);
      submissionData.append("paper_type", formData.paperType);
      submissionData.append("suggested_reviewer", formData.reviewer);
      submissionData.append("abstract", formData.abstract);
      submissionData.append("keywords", formData.keywords);
      submissionData.append("country", formData.country);

      // Files
      if (formData.copyrightFile) {
        submissionData.append("copyright_file", formData.copyrightFile);
      }

      if (formData.paperFile) {
        submissionData.append("paper_file", formData.paperFile);
      }

      // Multiple authors (convert to JSON string)
      const formattedAuthors = authors.map((a) => ({
        author_name: a.name,
        author_email: a.email,
      }));

      submissionData.append("authors", JSON.stringify(formattedAuthors));

      const response = await fetch(`${ApiUrl}/online-submission`, {
        method: "POST",
        body: submissionData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }

      Swal.fire({
        icon: "success",
        title: "Submitted Successfully!",
        text: "Your paper has been submitted successfully.",
        confirmButtonColor: "#3085d6",
      });

      copyrightRef.current.value = "";
      paperRef.current.value = "";

      // Reset form
      setFormData({
        paperTitle: "",
        authorName: "",
        authorEmail: "",
        authorInfo: "",
        address: "",
        country: "",
        paperType: "",
        reviewer: "",
        copyrightFile: null,
        paperFile: null,
        abstract: "",
        keywords: "",
        captcha: "",
      });

      setAuthors([{ name: "", email: "" }]);
      refreshCaptcha()

    } catch (error) {
      console.error("Submission Error:", error);
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error.message || "Something went wrong!",
      });
    }
  };

  return (
    <div className="ijacna-os-form-container">
      <h2 className="ijacna-os-form-title">Online Paper Submission</h2>

      <form className="ijacna-os-form-wrapper" onSubmit={handleSubmit}>
        <div className="ijacna-os-grid">
          {/* Left Column */}
          <div className="ijacna-os-column">
            <div className="ijacna-os-form-group">
              <label>
                Paper Title <span>*</span>
              </label>
              <input
                type="text"
                name="paperTitle"
                value={formData.paperTitle}

                className={`ijacna-os-input ${errors.paperTitle ? "error" : ""
                  }`}
                placeholder="Enter your paper title"
                onChange={handleChange}
              />
              {errors.paperTitle && (
                <p className="ijacna-os-error">{errors.paperTitle}</p>
              )}
            </div>

            <div className="ijacna-os-form-group">
              <label>
                Name of Corresponding Author <span>*</span>
              </label>
              <input
                type="text"
                name="authorName"
                value={formData.authorName}

                className={`ijacna-os-input ${errors.authorName ? "error" : ""
                  }`}
                placeholder="Author full name"
                onChange={handleChange}
              />
              {errors.authorName && (
                <p className="ijacna-os-error">{errors.authorName}</p>
              )}
            </div>

            <div className="ijacna-os-form-group">
              <label>
                Email of Corresponding Author <span>*</span>
              </label>
              <input
                type="email"
                name="authorEmail"
                value={formData.authorEmail}

                className={`ijacna-os-input ${errors.authorEmail ? "error" : ""
                  }`}
                placeholder="example@email.com"
                onChange={handleChange}
              />
              {errors.authorEmail && (
                <p className="ijacna-os-error">{errors.authorEmail}</p>
              )}
            </div>

            <div className="ijacna-os-form-group">
              <label>Corresponding Author Secondary Information</label>
              <input
                type="text"
                name="authorInfo"
                value={formData.authorInfo}

                placeholder="Phone, designation, etc."
                onChange={handleChange}
              />
            </div>

            {/* Authors Section */}
            {authors.length > 0 && (
              <div className="ijacna-os-authors-list">
                {authors.map((author, index) => (
                  <div key={index} className="ijacna-os-author-block">
                    <h4 className="ijacna-os-author-title">
                      Author {index + 1} :
                    </h4>

                    <div className="ijacna-os-form-group">
                      <label>Name of Author</label>
                      <input
                        type="text"
                        placeholder="Author name"
                        className={`ijacna-os-input ${errors[`authorName${index}`] ? "error" : ""
                          }`}
                        value={author.name}
                        onChange={(e) =>
                          handleAuthorChange(index, "name", e.target.value)
                        }
                      />
                      {errors[`authorName${index}`] && (
                        <p className="ijacna-os-error">{errors[`authorName${index}`]}</p>
                      )}
                    </div>

                    <div className="ijacna-os-form-group author-email-group">
                      <label>Email of Author</label>
                      <div className="ijacna-os-email-field">
                        <input
                          type="email"
                          placeholder="Author email"
                          className={`ijacna-os-input ${errors[`authorEmail${index}`] ? "error" : ""
                            }`}
                          value={author.email}
                          onChange={(e) =>
                            handleAuthorChange(index, "email", e.target.value)
                          }
                        />
                        <button
                          type="button"
                          className="ijacna-os-remove-btn"
                          onClick={() => removeAuthor(index)}
                        >
                          ❌
                        </button>
                      </div>
                      {errors[`authorEmail${index}`] && (
                        <p className="ijacna-os-error">{errors[`authorEmail${index}`]}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add author button */}
            <div
              style={{
                display: 'flex',
                gap: "20px",
              }}
            >
              <button
                type="button"
                className="ijacna-os-form-add-btn"
                onClick={addAuthor}
              >
                + Add New Author
              </button>
              <>
                {errors.authors && (
                  <p className="ijacna-os-error">{errors.authors}</p>
                )}
              </>
            </div>

            <div className="ijacna-os-form-group">
              <label>
                Address with Pin/Zip Code <span>*</span>
              </label>
              <textarea
                name="address"
                value={formData.address}

                className={`ijacna-os-input ${errors.address ? "error" : ""}`}
                placeholder="Enter complete address"
                onChange={handleChange}
              ></textarea>
              {errors.address && <p className="ijacna-os-error">{errors.address}</p>}
            </div>

            <div className="ijacna-os-form-group">
              <label>Country</label>

              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={`ijacna-os-input ${errors.country ? "error" : ""}`}
              >
                <option value="">Select Country</option>

                {countries.map((country, index) => (
                  <option key={index} value={country}>
                    {country}
                  </option>
                ))}
              </select>

              {errors.country && (
                <p className="ijacna-os-error">{errors.country}</p>
              )}
            </div>

          </div>

          {/* Right Column */}
          <div className="ijacna-os-column">
            <div className="ijacna-os-form-group">
              <label>
                Paper Type <span>*</span>
              </label>
              <input
                type="text"
                name="paperType"
                value={formData.paperType}

                className={`ijacna-os-input ${errors.paperType ? "error" : ""}`}
                placeholder="Research / Review / Case Study"
                onChange={handleChange}
              />
              {errors.paperType && <p className="ijacna-os-error">{errors.paperType}</p>}
            </div>

            <div className="ijacna-os-form-group">
              <label>
                Suggested Reviewer <span>*</span>
              </label>
              <input
                type="text"
                name="reviewer"
                value={formData.reviewer}

                className={`ijacna-os-input ${errors.reviewer ? "error" : ""}`}
                placeholder="Reviewer names and emails"
                onChange={handleChange}
              />
              {errors.reviewer && <p className="ijacna-os-error">{errors.reviewer}</p>}
            </div>

            <div className="ijacna-os-form-group">
              <label>
                Upload Scanned Copy of Copyright Form <span>*</span>
              </label>
              <div className={`ijacna-os-custom-file-upload ${errors.copyrightFile ? "error" : ""}`}>
                <label htmlFor="copyrightFile" className="ijacna-os-upload-btn">
                  Choose File
                </label>
                <span className="ijacna-os-file-name">
                  {formData.copyrightFile?.name || "No file chosen"}
                </span>
                <input
                  ref={copyrightRef}
                  id="copyrightFile"
                  type="file"
                  name="copyrightFile"
                  accept=".pdf,.jpg,.png"
                  onChange={handleChange}
                />
              </div>
              {errors.copyrightFile && (
                <p className="ijacna-os-error">{errors.copyrightFile}</p>
              )}
            </div>

            <div className="ijacna-os-form-group">
              <label>
                Upload Paper in MS Word (.doc or .docx) <span>*</span>
              </label>
              <div className={`ijacna-os-custom-file-upload ${errors.paperFile ? "error" : ""}`}>
                <label htmlFor="paperFile" className="ijacna-os-upload-btn">
                  Choose File
                </label>
                <span className="ijacna-os-file-name">
                  {formData.paperFile?.name || "No file chosen"}
                </span>
                <input
                  ref={paperRef}
                  id="paperFile"
                  type="file"
                  name="paperFile"
                  accept=".doc,.docx"
                  onChange={handleChange}
                />
              </div>
              {errors.paperFile && <p className="ijacna-os-error">{errors.paperFile}</p>}
            </div>

            <div className="ijacna-os-form-group">
              <label>Abstract</label>
              <textarea
                value={formData.abstract}
                name="abstract"
                placeholder="Brief abstract of your paper"
                onChange={handleChange}
              ></textarea>
            </div>

            <div className="ijacna-os-form-group">
              <label>Keywords</label>
              <textarea
                name="keywords"
                value={formData.keywords}

                placeholder="Enter keywords separated by commas"
                onChange={handleChange}
              ></textarea>
            </div>

            <div className="ijacna-os-form-captcha">
              <div className="ijacna-os-captcha-box">{captcha}</div>
              <button type="button" className="captcha-refresh-btn" onClick={refreshCaptcha}>
                {loading ? (
                  <span className="captcha-spinner"></span>
                ) : (
                  <FaSync className="captcha-refresh-icon" />
                )}
              </button>
              <input
                type="text"
                name="captcha"
                value={formData.captcha}

                className={`ijacna-os-input ${errors.captcha ? "error" : ""}`}
                placeholder="Enter the code"
                onChange={handleChange}
              />
            </div>
            {errors.captcha && <p className="ijacna-os-error">{errors.captcha}</p>}
            <button type="submit" className="ijacna-os-form-submit-btn">
              Submit Paper
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
